import React, { useEffect, useState } from "react";
import { Loader2, MonitorSmartphone, LogOut } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const DeviceSessions = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/sessions").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const signOutOthers = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/logout-others");
      toast.success(data.signed_out ? `Signed out ${data.signed_out} other device${data.signed_out === 1 ? "" : "s"}` : "No other devices were signed in");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not sign out the other devices");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="device-sessions" className="bg-card border border-border rounded-2xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
            <MonitorSmartphone className="w-4 h-4 text-primary" /> Devices &amp; sessions
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Your account can stay signed in on {data?.max_sessions ?? 2} devices. A newer sign-in
            automatically signs out the oldest one and we email you about it.
          </p>
        </div>
        <Button variant="outline" data-testid="signout-others-button" onClick={signOutOthers}
                disabled={busy} className="h-10 gap-2 font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
          Sign out other devices
        </Button>
      </div>

      <div className="mt-5 space-y-2">
        {!data ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : (
          data.sessions.map((s, i) => (
            <div key={s.id} data-testid={`session-row-${i}`}
                 className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border">
              <div>
                <div className="text-sm font-semibold">
                  {s.device || "Unknown device"}
                  {s.current && (
                    <span data-testid="current-session-badge" className="ml-2 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 text-[10px] font-bold uppercase tracking-wide">
                      This device
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {s.ip || "IP unknown"} · signed in {when(s.created_at)} · last used {when(s.last_seen)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
